/**
 * Admin → Incentives → "Deal closures" surface (Chunk E3).
 *
 * Four buckets:
 *   1. Pending confirmation — fully-paid clients with no ClientIncentive yet.
 *   2. In progress          — incentives awaiting sales / ops split.
 *   3. Locked / Paid        — approved + locked or fully paid out.
 *   4. By user              — admin picks a user, sees every closure they're
 *                              involved in, with personal totals.
 *
 * The top of the page renders a "this month" summary tile (committed, paid,
 * pending approvals, locked count, per-branch breakdown).
 *
 * Every list supports: search (client name / office branch), date range
 * filter, and CSV export of the visible rows.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
    CheckCircle2, Clock, Lock, Sparkles, X, AlertTriangle, ChevronRight,
    Search, Download, UserSearch, IndianRupee,
} from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../utils/api';

const TABS = [
    { id: 'pending',    label: 'Pending confirmation', icon: Sparkles },
    { id: 'inprogress', label: 'In progress',          icon: Clock },
    { id: 'locked',     label: 'Locked / Paid',        icon: Lock },
    { id: 'byuser',     label: 'By user',              icon: UserSearch },
];

export default function IncentiveDealClosures() {
    const [tab, setTab] = useState('pending');
    return (
        <div className="space-y-4">
            <div>
                <h2 className="font-bold text-lg">Deal closures</h2>
                <p className="text-xs text-ink-500">
                    A client moves into "Pending confirmation" the moment their approved payments add up to
                    the deal amount. Admin confirms the pool, then the assigned BDM splits sales and admin
                    splits operations. Locking writes a permanent audit row.
                </p>
            </div>

            <StatsTile />

            <div className="flex gap-2 flex-wrap">
                {TABS.map(t => (
                    <button
                        key={t.id}
                        onClick={() => setTab(t.id)}
                        className={`ag-tab ${tab === t.id ? 'ag-tab-active' : 'ag-tab-inactive'}`}
                    >
                        <t.icon className="h-3.5 w-3.5" /> {t.label}
                    </button>
                ))}
            </div>

            {tab === 'pending'    && <PendingList />}
            {tab === 'inprogress' && <IncentiveList statuses={['awaiting_sales_split', 'awaiting_ops_split']} />}
            {tab === 'locked'     && <IncentiveList statuses={['locked', 'paid']} />}
            {tab === 'byuser'     && <ByUserPanel />}
        </div>
    );
}

/* ─────────── Top-of-page roll-up tile ─────────── */
function StatsTile() {
    const [stats, setStats] = useState(null);
    useEffect(() => { api.get('/client-incentives/stats').then(r => setStats(r.data)); }, []);
    if (!stats) return null;

    return (
        <div className="ag-card p-5 space-y-3">
            <div className="flex items-baseline justify-between flex-wrap gap-2">
                <h3 className="font-bold inline-flex items-center gap-2">
                    <IndianRupee className="h-4 w-4 text-brand-teal-700" /> {stats.monthLabel}
                </h3>
                <span className="text-[11px] text-ink-500">Rolled up across the whole company.</span>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <Mini label="Committed this month" value={`₹ ${stats.committedThisMonth.toLocaleString('en-IN')}`} />
                <Mini label="Paid this month"      value={`₹ ${stats.paidThisMonth.toLocaleString('en-IN')}`} />
                <Mini label="Awaiting split"       value={stats.pendingApprovalCnt} />
                <Mini label="Locked, unpaid"       value={stats.lockedCnt} />
            </div>
            {stats.byBranch?.length > 0 && (
                <div>
                    <div className="text-[10px] uppercase tracking-wider text-ink-500 font-semibold mb-1">By branch</div>
                    <div className="flex flex-wrap gap-2">
                        {stats.byBranch.map(b => (
                            <span key={b.branch} className="ag-chip bg-ink-100 text-ink-700">
                                {b.branch} · ₹ {b.total.toLocaleString('en-IN')}
                                <span className="text-ink-500 ml-1">({b.count})</span>
                            </span>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
function Mini({ label, value }) {
    return (
        <div className="rounded-lg bg-ink-50 p-3">
            <div className="text-[10px] uppercase tracking-wider text-ink-500 font-semibold">{label}</div>
            <div className="text-lg font-extrabold mt-0.5 tabular-nums">{value}</div>
        </div>
    );
}

/* ─────────── Pending confirmation ─────────── */
function PendingList() {
    const [rows, setRows] = useState(null);
    const [confirming, setConfirming] = useState(null);
    const [q, setQ] = useState('');

    const load = () => api.get('/client-incentives/pending').then(r => setRows(r.data.rows || []));
    useEffect(() => { load(); }, []);

    const filtered = useMemo(() => {
        if (!q.trim() || !rows) return rows || [];
        const rx = new RegExp(q.trim(), 'i');
        return rows.filter(r =>
            rx.test(r.clientName || '') ||
            rx.test(r.salesTeam?.bdm?.name || '') ||
            rx.test(r.salesTeam?.bda?.name || '') ||
            rx.test(r.salesTeam?.bde?.name || '')
        );
    }, [rows, q]);

    if (rows === null) return <Skeleton />;
    if (rows.length === 0) {
        return (
            <div className="ag-card p-8 text-center text-sm text-ink-500">
                <CheckCircle2 className="h-7 w-7 mx-auto text-emerald-500 mb-2" />
                Nothing waiting. All fully-paid clients have been confirmed.
            </div>
        );
    }

    const exportCsv = () => downloadCsv('pending-confirmation', filtered.map(r => ({
        Client: r.clientName || '',
        Deal: r.dealAmount,
        Paid: r.paid,
        BDM: r.salesTeam?.bdm?.name || '',
        BDE: r.salesTeam?.bde?.name || '',
        BDA: r.salesTeam?.bda?.name || '',
        Submitted: r.submittedAt ? new Date(r.submittedAt).toLocaleDateString() : '',
    })));

    return (
        <>
            <FilterBar q={q} onQ={setQ} count={filtered.length} total={rows.length} onExport={exportCsv} />

            <div className="ag-card p-0 overflow-x-auto">
                <table className="ag-table">
                    <thead><tr>
                        <th>Client</th><th>Deal</th><th>Paid</th><th>BDM</th><th>BDE</th><th>BDA</th><th></th>
                    </tr></thead>
                    <tbody>
                        {filtered.map(r => (
                            <tr key={r.submissionId}>
                                <td className="font-medium">{r.clientName}</td>
                                <td className="tabular-nums">₹ {(r.dealAmount || 0).toLocaleString('en-IN')}</td>
                                <td className="tabular-nums">₹ {(r.paid || 0).toLocaleString('en-IN')}</td>
                                <td className="text-xs">{r.salesTeam?.bdm?.name || <Missing />}</td>
                                <td className="text-xs">{r.salesTeam?.bde?.name || <Missing />}</td>
                                <td className="text-xs">{r.salesTeam?.bda?.name || <Missing />}</td>
                                <td className="text-right">
                                    <button
                                        className="ag-btn-primary text-xs"
                                        disabled={!r.salesTeam?.bda || !r.salesTeam?.bde || !r.salesTeam?.bdm}
                                        title={!r.salesTeam?.bda || !r.salesTeam?.bde || !r.salesTeam?.bdm
                                            ? 'Set BDA / BDE / BDM on the client page first'
                                            : ''}
                                        onClick={() => setConfirming(r)}
                                    >
                                        Confirm &amp; set pool <ChevronRight className="h-3.5 w-3.5" />
                                    </button>
                                </td>
                            </tr>
                        ))}
                        {filtered.length === 0 && (
                            <tr><td colSpan={7} className="text-center text-ink-500 py-8">No matches.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>

            {confirming && (
                <ConfirmModal
                    row={confirming}
                    onClose={(saved) => {
                        setConfirming(null);
                        if (saved) {
                            toast.success(`Incentive created. Pool ₹${saved.poolAmount.toLocaleString('en-IN')}.`);
                            load();
                        }
                    }}
                />
            )}
        </>
    );
}

function Missing() {
    return <span className="text-rose-600 inline-flex items-center gap-1 text-[11px] font-semibold"><AlertTriangle className="h-3 w-3" /> not set</span>;
}

/* ─────────── Confirm modal ─────────── */
function ConfirmModal({ row, onClose }) {
    const [defaults, setDefaults] = useState(null);
    const [percent, setPercent]   = useState('');
    const [busy, setBusy]         = useState(false);

    useEffect(() => {
        api.get('/settings').then(r => {
            const inc = r.data.incentive || {};
            setDefaults(inc);
            setPercent(String(inc.defaultPoolPercent ?? 2));
        });
    }, []);

    const poolBase = defaults?.poolBase === 'final_payable' ? row.finalPayable : row.dealAmount;
    const pct      = Math.max(0, Math.min(100, Number(percent) || 0));
    const preview  = Math.round((poolBase * pct) / 100);

    const submit = async () => {
        setBusy(true);
        try {
            const { data } = await api.post(`/client-incentives/confirm/${row.submissionId}`, { poolPercent: pct });
            onClose(data);
        } catch {/* toasted */}
        finally { setBusy(false); }
    };

    return (
        <ModalShell onClose={() => onClose(null)} title="Confirm pool & create incentive">
            <KV k="Client"      v={row.clientName} />
            <KV k="Deal amount" v={`₹ ${(row.dealAmount || 0).toLocaleString('en-IN')}`} />
            {defaults?.poolBase === 'final_payable' && (
                <KV k="Final payable" v={`₹ ${(row.finalPayable || 0).toLocaleString('en-IN')}`} />
            )}
            <KV k="Pool base"   v={defaults?.poolBase === 'final_payable' ? 'Final payable (post GST/TDS)' : 'Deal amount (gross)'} />

            <div className="mt-3">
                <label className="ag-label">Pool % (default {defaults?.defaultPoolPercent ?? 2}%)</label>
                <input
                    type="number" className="ag-input" step="0.01" min="0" max="100"
                    value={percent} onChange={e => setPercent(e.target.value)}
                />
                <div className="text-[11px] text-ink-500 mt-1">
                    Override only for negotiated deals. The default lives in Settings → Incentive.
                </div>
            </div>

            <div className="mt-3 rounded-lg bg-brand-teal-50 text-brand-teal-700 p-3 text-sm flex items-center justify-between">
                <span>Pool</span>
                <span className="font-bold tabular-nums">₹ {preview.toLocaleString('en-IN')}</span>
            </div>

            <div className="flex justify-end gap-2 pt-2">
                <button className="ag-btn-ghost" onClick={() => onClose(null)}>Cancel</button>
                <button className="ag-btn-primary" disabled={busy || pct <= 0} onClick={submit}>
                    {busy ? 'Creating…' : 'Confirm & create'}
                </button>
            </div>
        </ModalShell>
    );
}

/* ─────────── In progress / Locked list ─────────── */
function IncentiveList({ statuses }) {
    const [rows, setRows] = useState(null);
    const [q, setQ]       = useState('');
    const [from, setFrom] = useState('');
    const [to,   setTo]   = useState('');

    useEffect(() => {
        const params = {};
        if (q.trim()) params.q = q.trim();
        if (from)     params.from = from;
        if (to)       params.to   = to;
        api.get('/client-incentives', { params })
           .then(r => {
                const filtered = (r.data.rows || []).filter(x => statuses.includes(x.status));
                setRows(filtered);
            });
    }, [statuses.join(','), q, from, to]);

    if (rows === null) return <Skeleton />;

    const exportCsv = () => downloadCsv(`closures-${statuses.join('-')}`, rows.map(r => ({
        Client: r.clientName || '',
        Status: r.status,
        Deal: r.dealAmount,
        'Pool %': r.poolPercent,
        Pool: r.poolAmount,
        'Sales %': pct(r),
        'Ops %': opsPct(r),
        BDM: r.salesTeam?.bdm?.name || '',
        Branch: r.officeBranch || '',
        Created: r.createdAt ? new Date(r.createdAt).toLocaleDateString() : '',
        Paid: r.paidAt ? new Date(r.paidAt).toLocaleDateString() : '',
    })));

    return (
        <>
            <FilterBar q={q} onQ={setQ} from={from} onFrom={setFrom} to={to} onTo={setTo} count={rows.length} onExport={exportCsv} showDate />

            {rows.length === 0
                ? <div className="ag-card p-8 text-center text-sm text-ink-500">Nothing matches that filter.</div>
                : (
                    <div className="ag-card p-0 overflow-x-auto">
                        <table className="ag-table">
                            <thead><tr>
                                <th>Status</th><th>Client</th><th>Deal</th><th>Pool</th>
                                <th>Sales split</th><th>Ops split</th><th>BDM</th><th>Branch</th><th>Created</th><th></th>
                            </tr></thead>
                            <tbody>
                                {rows.map(r => (
                                    <tr key={r._id}>
                                        <td><StatusChip status={r.status} /></td>
                                        <td className="font-medium">{r.clientName || '—'}</td>
                                        <td className="tabular-nums">₹ {(r.dealAmount || 0).toLocaleString('en-IN')}</td>
                                        <td className="tabular-nums">₹ {(r.poolAmount || 0).toLocaleString('en-IN')} <span className="text-[11px] text-ink-500">({r.poolPercent}%)</span></td>
                                        <td className="text-xs">{r.salesAllocations?.length ? `${pct(r)}% used` : '—'}</td>
                                        <td className="text-xs">{r.opsAllocations?.length   ? `${opsPct(r)}%` : '—'}</td>
                                        <td className="text-xs">{r.salesTeam?.bdm?.name || '—'}</td>
                                        <td className="text-xs">{r.officeBranch || '—'}</td>
                                        <td className="text-xs text-ink-500">{r.createdAt ? new Date(r.createdAt).toLocaleDateString() : '—'}</td>
                                        <td className="text-right">
                                            <Link className="ag-btn-ghost text-xs" to={`/admin/incentives/closures/${r._id}`}>
                                                Open <ChevronRight className="h-3.5 w-3.5" />
                                            </Link>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
        </>
    );
}

/* ─────────── By-user panel ─────────── */
function ByUserPanel() {
    const [users, setUsers] = useState([]);
    const [userId, setUserId] = useState('');
    const [data, setData]     = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        /* Pull everyone with a sales designation set — that's the meaningful
           population. Admin can also pick themselves via free-text search. */
        api.get('/users/picker').then(r => setUsers(r.data.items || []));
    }, []);

    useEffect(() => {
        if (!userId) { setData(null); return; }
        setLoading(true);
        api.get(`/client-incentives/by-user/${userId}`)
           .then(r => setData(r.data))
           .finally(() => setLoading(false));
    }, [userId]);

    const exportCsv = () => {
        if (!data) return;
        downloadCsv(`incentives-${data.user.email || 'user'}`, data.rows.map(r => ({
            Client: r.clientName || '',
            Status: r.status,
            Deal: r.dealAmount,
            Pool: r.poolAmount,
            'Your role': r.myAllocation?.role || '—',
            'Your %':    r.myAllocation?.percent || 0,
            'Your ₹':    r.myAllocation?.amount || 0,
            Created: r.createdAt ? new Date(r.createdAt).toLocaleDateString() : '',
            Paid:    r.paidAt ? new Date(r.paidAt).toLocaleDateString() : '',
        })));
    };

    return (
        <div className="space-y-3">
            <div className="ag-card p-4 flex items-center gap-3 flex-wrap">
                <UserSearch className="h-4 w-4 text-ink-500" />
                <label className="text-sm font-semibold">Pick a user</label>
                <select className="ag-input flex-1 min-w-[260px]" value={userId} onChange={e => setUserId(e.target.value)}>
                    <option value="">— select —</option>
                    {users.map(u => (
                        <option key={u._id} value={u._id}>
                            {u.name} · {u.email}{u.salesDesignation ? ` · ${u.salesDesignation.toUpperCase()}` : ''}{u.officeBranch ? ` · ${u.officeBranch}` : ''}
                        </option>
                    ))}
                </select>
                {data && (
                    <button className="ag-btn-ghost text-xs" onClick={exportCsv}>
                        <Download className="h-3.5 w-3.5" /> Export CSV
                    </button>
                )}
            </div>

            {loading && <Skeleton />}

            {data && !loading && (
                <>
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                        <Mini label="Closures involved"   value={data.totals.count} />
                        <Mini label="Total (all)"         value={`₹ ${data.totals.all.toLocaleString('en-IN')}`} />
                        <Mini label="Locked or paid"      value={`₹ ${data.totals.lockedOrPaid.toLocaleString('en-IN')}`} />
                        <Mini label="Paid out"            value={`₹ ${data.totals.paid.toLocaleString('en-IN')}`} />
                    </div>

                    <div className="ag-card p-0 overflow-x-auto">
                        <table className="ag-table">
                            <thead><tr>
                                <th>Status</th><th>Client</th><th>Role</th><th>%</th><th>Amount</th><th>Created</th><th></th>
                            </tr></thead>
                            <tbody>
                                {data.rows.map(r => (
                                    <tr key={r._id}>
                                        <td><StatusChip status={r.status} /></td>
                                        <td className="font-medium">{r.clientName || '—'}</td>
                                        <td className="text-xs uppercase tracking-wider text-ink-500">{r.myAllocation?.role || '—'}</td>
                                        <td className="tabular-nums">{r.myAllocation?.percent?.toFixed(2) || '0.00'}%</td>
                                        <td className="tabular-nums">₹ {(r.myAllocation?.amount || 0).toLocaleString('en-IN')}</td>
                                        <td className="text-xs text-ink-500">{r.createdAt ? new Date(r.createdAt).toLocaleDateString() : '—'}</td>
                                        <td className="text-right">
                                            <Link className="ag-btn-ghost text-xs" to={`/admin/incentives/closures/${r._id}`}>
                                                Open <ChevronRight className="h-3.5 w-3.5" />
                                            </Link>
                                        </td>
                                    </tr>
                                ))}
                                {data.rows.length === 0 && (
                                    <tr><td colSpan={7} className="text-center text-ink-500 py-8">This user has no closure involvement yet.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </>
            )}
        </div>
    );
}

/* ─────────── Filter bar (search + dates + export) ─────────── */
function FilterBar({ q, onQ, from, onFrom, to, onTo, count, total, onExport, showDate }) {
    return (
        <div className="ag-card p-3 flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-2 flex-1 min-w-[200px]">
                <Search className="h-4 w-4 text-ink-500" />
                <input
                    className="ag-input flex-1"
                    placeholder="Search client name, branch, BDM…"
                    value={q}
                    onChange={e => onQ(e.target.value)}
                />
            </div>
            {showDate && (
                <>
                    <input type="date" className="ag-input w-auto text-xs" value={from || ''} onChange={e => onFrom(e.target.value)} title="From date" />
                    <input type="date" className="ag-input w-auto text-xs" value={to   || ''} onChange={e => onTo(e.target.value)}   title="To date" />
                </>
            )}
            <span className="text-xs text-ink-500 shrink-0">
                {typeof total === 'number' ? `${count} of ${total}` : `${count}`}
            </span>
            <button className="ag-btn-ghost text-xs" onClick={onExport} disabled={!count}>
                <Download className="h-3.5 w-3.5" /> Export CSV
            </button>
        </div>
    );
}

/* ─────────── CSV download helper ─────────── */
function downloadCsv(filenameBase, rows) {
    if (!rows || rows.length === 0) { toast.error('Nothing to export'); return; }
    const headers = Object.keys(rows[0]);
    const escape  = (v) => {
        const s = v === null || v === undefined ? '' : String(v);
        if (s.includes(',') || s.includes('"') || s.includes('\n')) {
            return `"${s.replace(/"/g, '""')}"`;
        }
        return s;
    };
    const csv = [headers.join(','), ...rows.map(r => headers.map(h => escape(r[h])).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = `${filenameBase}-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
}

function pct(r)    { return ((r.salesAllocations || []).reduce((s, a) => s + (a.percent || 0), 0)).toFixed(2); }
function opsPct(r) { return ((r.opsAllocations   || []).reduce((s, a) => s + (a.percent || 0), 0)).toFixed(2); }

/* ─────────── Shared bits ─────────── */
export function StatusChip({ status }) {
    const map = {
        pending_confirmation: ['bg-amber-100 text-amber-800',   'Pending confirmation'],
        awaiting_sales_split: ['bg-sky-100 text-sky-800',       'Sales split pending'],
        awaiting_ops_split:   ['bg-violet-100 text-violet-800', 'Ops split pending'],
        locked:               ['bg-emerald-100 text-emerald-800', 'Locked'],
        paid:                 ['bg-ink-300/30 text-ink-700',    'Paid'],
    };
    const [cls, label] = map[status] || ['bg-ink-100 text-ink-700', status];
    return <span className={`ag-chip ${cls}`}>{label}</span>;
}

export function KV({ k, v }) {
    return (
        <div className="flex justify-between items-baseline py-1.5 border-b border-ink-100 last:border-0">
            <div className="text-xs text-ink-500">{k}</div>
            <div className="text-sm font-medium text-right max-w-[60%] truncate">{v}</div>
        </div>
    );
}

export function ModalShell({ title, onClose, children, width = 'max-w-md' }) {
    return (
        <div className="fixed inset-0 z-40 bg-ink-900/40 grid place-items-center p-4" onClick={onClose}>
            <div className={`bg-white rounded-xl2 shadow-soft w-full ${width} p-5 space-y-2`} onClick={e => e.stopPropagation()}>
                <div className="flex items-center justify-between">
                    <h3 className="font-bold text-lg">{title}</h3>
                    <button onClick={onClose} className="text-ink-500 hover:text-brand-red"><X className="h-4 w-4" /></button>
                </div>
                {children}
            </div>
        </div>
    );
}

function Skeleton() {
    return <div className="ag-card p-8 text-center text-sm text-ink-500">Loading…</div>;
}
